import React, { useState, useEffect } from 'react';
import { Gift, Sparkles, Check, Clock, X, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { formatEUR } from '../utils/format';

const MyCoupons = () => {
  const [coupons, setCoupons] = useState({ active: [], used: [], expired: [] });
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(null);

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/my-coupons`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      }
    } catch (error) {
      console.error('Error loading coupons:', error);
      toast.error('Fehler beim Laden der Coupons');
    } finally {
      setLoading(false);
    }
  };

  const revealCoupon = async (couponId) => {
    setRevealing(couponId);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/reveal/${couponId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message, {
          icon: '🎉',
          duration: 3000
        });
        
        // Reload coupons
        await loadCoupons();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Aktivieren');
      }
    } catch (error) {
      console.error('Error revealing coupon:', error);
      toast.error('Fehler beim Aktivieren des Coupons');
    } finally {
      setRevealing(null);
    }
  };

  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon-Code kopiert!', { icon: '📋' });
  };

  const CouponCard = ({ coupon, type }) => {
    const isRevealed = coupon.is_revealed;
    const isUsed = coupon.is_used;
    
    // Calculate expiry
    const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;
    const daysLeft = validUntil ? Math.ceil((validUntil - new Date()) / (1000 * 60 * 60 * 24)) : null;

    return (
      <Card className={`relative overflow-hidden transition-all ${
        isUsed ? 'opacity-60 bg-gray-50' : 
        !isRevealed ? 'bg-gradient-to-br from-purple-50 to-pink-50 hover:shadow-lg' :
        'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200'
      }`}>
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <Gift className={`h-6 w-6 ${
                isUsed ? 'text-gray-400' : 
                !isRevealed ? 'text-purple-600' : 
                'text-green-600'
              }`} />
              <div>
                <h3 className="font-bold text-lg">{coupon.description}</h3>
                <p className="text-xs text-gray-600">
                  {coupon.coupon_type === 'welcome' ? '🎁 Willkommens-Coupon' :
                   coupon.coupon_type === 'christmas' ? '🎄 Weihnachts-Special' :
                   coupon.coupon_type === 'birthday' ? '🎂 Geburtstags-Geschenk' :
                   coupon.coupon_type === 'loyalty' ? '⭐ Treue-Bonus' :
                   '🎫 Sonder-Coupon'}
                </p>
              </div>
            </div>
            
            {isUsed && (
              <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                Verwendet
              </span>
            )}
          </div>

          {/* Coupon Value - Hidden until revealed */}
          {!isRevealed ? (
            <div className="relative">
              <div className="bg-gradient-to-r from-purple-200 to-pink-200 rounded-lg p-6 mb-4 text-center">
                <div className="flex flex-col items-center gap-2">
                  <Sparkles className="h-12 w-12 text-purple-600 animate-pulse" />
                  <p className="text-sm font-medium text-purple-800">
                    Klicken Sie "Aktivieren" um Ihren Rabatt freizuschalten!
                  </p>
                </div>
              </div>
              
              <Button
                onClick={() => revealCoupon(coupon.id)}
                disabled={revealing === coupon.id}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                size="lg"
              >
                {revealing === coupon.id ? (
                  <>
                    <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                    Aktiviere...
                  </>
                ) : (
                  <>
                    <Gift className="h-4 w-4 mr-2" />
                    Coupon Aktivieren
                  </>
                )}
              </Button>
            </div>
          ) : (
            <>
              {/* Revealed Coupon */}
              <div className="bg-white rounded-lg border-2 border-dashed border-green-400 p-4 mb-4">
                <div className="text-center mb-2">
                  <div className="text-4xl font-bold text-green-600 mb-1">
                    {coupon.discount_value}%
                  </div>
                  <div className="text-sm text-gray-600">Rabatt auf Ihre Bestellung</div>
                </div>
                
                <div className="bg-gray-50 rounded px-3 py-2 flex items-center justify-between">
                  <code className="font-mono font-bold text-lg text-gray-800">
                    {coupon.code}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyCouponCode(coupon.code)}
                  >
                    <Tag className="h-4 w-4 mr-1" />
                    Kopieren
                  </Button>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Tag className="h-4 w-4" />
                  <span>Mindestbestellwert: {formatEUR(coupon.min_order_value)}</span>
                </div>
                
                {daysLeft !== null && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>
                      {daysLeft > 0 
                        ? `Noch ${daysLeft} Tag${daysLeft !== 1 ? 'e' : ''} gültig`
                        : 'Heute letzter Tag!'}
                    </span>
                  </div>
                )}
              </div>

              {!isUsed && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-gray-700">
                  💡 <strong>Tipp:</strong> Geben Sie den Code beim Checkout ein, um den Rabatt zu erhalten.
                </div>
              )}
            </>
          )}

          {/* Used Info */}
          {isUsed && coupon.used_at && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Verwendet am {new Date(coupon.used_at).toLocaleDateString('de-DE')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Sparkles className="h-12 w-12 text-purple-600 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">Lade Ihre Coupons...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const totalCoupons = coupons.active.length + coupons.used.length + coupons.expired.length;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Gift className="h-10 w-10 text-purple-600" />
              <h1 className="text-4xl font-bold">Meine Coupons</h1>
            </div>
            <p className="text-gray-600">
              Aktivieren Sie Ihre Coupons und sparen Sie bei Ihrer nächsten Bestellung
            </p>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-green-600">{coupons.active.length}</div>
                <div className="text-sm text-gray-600">Verfügbare Coupons</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-gray-600">{coupons.used.length}</div>
                <div className="text-sm text-gray-600">Verwendete Coupons</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-purple-600">{totalCoupons}</div>
                <div className="text-sm text-gray-600">Gesamt</div>
              </CardContent>
            </Card>
          </div>

          {/* Coupons Tabs */}
          <Tabs defaultValue="active">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="active">
                Verfügbar ({coupons.active.length})
              </TabsTrigger>
              <TabsTrigger value="used">
                Verwendet ({coupons.used.length})
              </TabsTrigger>
              <TabsTrigger value="expired">
                Abgelaufen ({coupons.expired.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {coupons.active.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500">
                    <Gift className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p>Keine verfügbaren Coupons</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {coupons.active.map(coupon => (
                    <CouponCard key={coupon.id} coupon={coupon} type="active" />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="used">
              {coupons.used.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500">
                    <Check className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p>Noch keine verwendeten Coupons</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {coupons.used.map(coupon => (
                    <CouponCard key={coupon.id} coupon={coupon} type="used" />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="expired">
              {coupons.expired.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500">
                    <Clock className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p>Keine abgelaufenen Coupons</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {coupons.expired.map(coupon => (
                    <CouponCard key={coupon.id} coupon={coupon} type="expired" />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyCoupons;
